import { useState } from "react";
import { X } from "lucide-react";

import { leaveApi } from "../api";
import { getErrorMessage } from "../hooks";

export type AdjustBalanceTarget = {
  employeeId: number;
  employeeName: string;
  leaveTypeId: number;
  leaveTypeName: string;
  leaveYear: number;
  currentEntitledDays: number;
  currentBroughtForwardDays: number;
  currentUsedDays: number;
};

/**
 * Manually sets an employee's entitled + brought-forward days -- and, optionally,
 * days already used -- for one leave type/year. Wires up
 * LeaveBalanceService.adjustEntitlement -- previously unreachable from any UI.
 * For one-off opening-balance loads (days banked, or already taken, before
 * go-live) and corrections; routine balances still come from the year-end
 * rollover or the normal apply/approve workflow.
 *
 * Used Days defaults to the balance's current figure and is left exactly as-is
 * unless you change it, so opening a correction for Entitled/Brought Forward
 * alone (the common case) never accidentally touches Used Days.
 */
export function AdjustBalanceModal({
  target,
  onClose,
  onSaved,
}: {
  target: AdjustBalanceTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [entitledDays, setEntitledDays] = useState(String(target.currentEntitledDays));
  const [broughtForwardDays, setBroughtForwardDays] = useState(String(target.currentBroughtForwardDays));
  const [usedDays, setUsedDays] = useState(String(target.currentUsedDays));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);

    const entitled = Number(entitledDays);
    const broughtForward = Number(broughtForwardDays);
    const used = Number(usedDays);

    if (
      !Number.isFinite(entitled) || entitled < 0 ||
      !Number.isFinite(broughtForward) || broughtForward < 0 ||
      !Number.isFinite(used) || used < 0
    ) {
      setError("Enter valid, non-negative numbers for all fields.");
      return;
    }

    setSaving(true);

    try {
      await leaveApi.adjustLeaveBalance({
        employeeId: target.employeeId,
        leaveTypeId: target.leaveTypeId,
        leaveYear: target.leaveYear,
        entitledDays: entitled,
        broughtForwardDays: broughtForward,
        // Only sent when it actually changed from what the balance already had, so a
        // plain Entitled/Brought Forward correction never touches Used Days by accident.
        usedDays: used !== target.currentUsedDays ? used : undefined,
      });
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-black text-slate-900">Adjust Leave Balance</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {target.employeeName} — {target.leaveTypeName} ({target.leaveYear})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Entitled Days
            </span>
            <input
              type="number"
              min={0}
              step="0.5"
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={entitledDays}
              onChange={(e) => setEntitledDays(e.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Brought Forward Days
            </span>
            <input
              type="number"
              min={0}
              step="0.5"
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={broughtForwardDays}
              onChange={(e) => setBroughtForwardDays(e.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Used Days
            </span>
            <input
              type="number"
              min={0}
              step="0.5"
              className="block w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
              value={usedDays}
              onChange={(e) => setUsedDays(e.target.value)}
            />
            <span className="block text-xs text-slate-400">
              Days this employee has already taken this year. Leave as-is unless you're
              loading an opening balance for someone with leave already taken.
            </span>
          </label>

          {error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
